import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import EditorialBanner from "@/components/EditorialBanner";
import Marquee from "@/components/Marquee";
import HelpFAB from "@/components/HelpFAB";
import { Toaster } from "sonner";
import { MOBILE_MQ, isMobileViewport } from "@/lib/mobile";

const Manifesto = lazy(() => import("@/components/Manifesto"));
const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const ServiceFAQ = lazy(() => import("@/components/ServiceFAQ"));
const Footer = lazy(() => import("@/components/Footer"));

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function App() {
  const page = typeof window !== "undefined" ? window.PL_PAGE_CONTENT : null;
  const isEditorialPage = page?.lockedQuoteCategory === "editorial";
  const [isMobile, setIsMobile] = useState(isMobileViewport);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    mq.addListener?.(onChange);
    return () => {
      mq.removeEventListener?.("change", onChange);
      mq.removeListener?.(onChange);
    };
  }, []);

  useEffect(() => {
    if (!page) return;
    if (page.documentTitle) document.title = page.documentTitle;
    upsertMeta("name", "description", page.metaDescription);
    upsertMeta("name", "keywords", page.metaKeywords);
    upsertMeta("name", "robots", page.robots || "index, follow, max-snippet:-1, max-image-preview:large");
    upsertMeta("property", "og:title", page.ogTitle || page.documentTitle);
    upsertMeta("property", "og:description", page.ogDescription || page.metaDescription);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:locale", "pt_BR");
    upsertMeta("property", "og:site_name", "Print Livros");
    upsertMeta("property", "og:url", page.canonical);
    upsertMeta("property", "og:image", page.ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", page.ogTitle || page.documentTitle);
    upsertMeta("name", "twitter:description", page.ogDescription || page.metaDescription);
    if (page.canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", page.canonical);
    }
  }, [page]);

  useEffect(() => {
    // Lenis = rAF contínuo — NUNCA no mobile (trava / tela branca no scroll iOS)
    window.__lenis = null;
    if (isMobile || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    let destroyed = false;
    let raf;
    let lenis;
    import("lenis").then(({ default: Lenis }) => {
      if (destroyed) return;
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      window.__lenis = lenis;
    });
    return () => {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      lenis?.destroy?.();
      window.__lenis = null;
    };
  }, [isMobile]);

  useEffect(() => {
    if (document.getElementById("pl-modelo-fonts")) return undefined;
    const link = document.createElement("link");
    link.id = "pl-modelo-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Playfair+Display:ital,wght@0,600;0,800;1,600&family=Anton&display=swap";
    document.head.appendChild(link);
    return undefined;
  }, []);

  return (
    <div className={`min-h-screen bg-[#F8F7F4] text-neutral-900 ${isMobile ? "overflow-x-hidden pl-mobile" : "overflow-x-clip"}`}>
      {!isMobile && <div className="grain-overlay" aria-hidden="true" />}
      <Navbar />
      <main>
        {isEditorialPage ? <EditorialBanner /> : <Hero />}
        <Marquee />
        <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
          {isEditorialPage ? (
            <>
              <QuoteForm />
              <Manifesto />
            </>
          ) : (
            <>
              <Manifesto />
              <QuoteForm />
            </>
          )}
          <ServiceFAQ />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <HelpFAB />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
